import { useEffect, useState } from "react";
import {
  matchesConfirmPhrase,
  resolveDeleteMode,
  resolveDestructiveLabel,
  resolveEffectiveScope,
  resolveRequiredPhrase
} from "../helpers/deleteMapping.helpers";
import { DeleteMappingMode, DeleteMappingScope } from "../types/deleteMapping.types";

interface UseDeleteMappingDialogArgs {
  open: boolean;
  versionCount: number;
  currentVersionId: string | undefined;
}

interface UseDeleteMappingDialogResult {
  scope: DeleteMappingScope;
  selectScope: (scope: DeleteMappingScope) => void;
  confirmText: string;
  setConfirmText: (value: string) => void;
  requiredPhrase: string;
  isConfirmed: boolean;
  destructiveLabel: string;
  mode: DeleteMappingMode;
}

/**
 * Owns the dialog's two pieces of state. Plain `useState` rather than react-hook-form: there is
 * no payload being built and no validation schema, only an action gate.
 */
export function useDeleteMappingDialog({
  open,
  versionCount,
  currentVersionId
}: UseDeleteMappingDialogArgs): UseDeleteMappingDialogResult {
  const mode = resolveDeleteMode(versionCount, currentVersionId);
  const [selectedScope, setSelectedScope] = useState<DeleteMappingScope>(DeleteMappingScope.CurrentVersion);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (open) {
      setSelectedScope(DeleteMappingScope.CurrentVersion);
      setConfirmText("");
    }
    // Only re-seed when the dialog transitions to open, so a version-count refetch cannot move
    // the selection out from under the user mid-typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /** Changing scope changes the required phrase, so anything already typed is now wrong. */
  const selectScope = (next: DeleteMappingScope): void => {
    setSelectedScope(next);
    setConfirmText("");
  };

  // The count and current-version id can land after the dialog is already open. Deriving mode
  // and scope from them means that flip re-enables whichever option just became legitimate and
  // re-derives the default, instead of leaving the dialog stranded on a selection the user never
  // made or aimed at a scope this mode has disabled. Any phrase already typed simply stops
  // matching, which disables the button -- the safe direction.
  const scope = resolveEffectiveScope(selectedScope, mode);
  const requiredPhrase = resolveRequiredPhrase(scope);

  return {
    scope,
    selectScope,
    confirmText,
    setConfirmText,
    requiredPhrase,
    isConfirmed: matchesConfirmPhrase(confirmText, requiredPhrase),
    destructiveLabel: resolveDestructiveLabel(scope),
    mode
  };
}
